import { useScanPayPoint } from "Api/Mutations/PayPoints/useScanPayPoint";
import { usePISPAuthenticate } from "Api/Mutations/Transactions/usePISPAuthenticate";
import { useTransactionPay } from "Api/Mutations/Transactions/useTransactionPay";
import type {
  BankAccountListItem,
  ErrorResponse,
} from "Infrastructure/Api/Api";
import { useRef, useState } from "react";
import { useResource } from "Translations/Resources";
import {
  type ErrorType,
  getTransactionErrorMessage,
  handleTransactionPayError,
} from "Utils/TransactionErrorsUtils";

type SubmitArgs = {
  paypointPublicID: string;
  bankAccount: BankAccountListItem;
  amount: number;
  tipAmount: number;
  onError: (message: string | undefined) => void;
};

type LastScan = {
  paypointPublicID: string;
  amount: number;
  tipAmount: number;
  transactionPublicID: string;
};

// Variable Collector entry: /scan creates the transaction with the customer-chosen amount + tip,
// then /pay charges it. Mirrors usePaymentSubmit but without a /tip step (the /scan call already
// persisted both amount and tip in one go).
//
// We cache the last /scan keyed by (paypoint, amount, tip) so that a /pay failure followed by
// a retry on the same inputs reuses the existing transaction instead of spawning a new one
// (matching Fixed Collector behavior). Any input change invalidates the cache and triggers
// a fresh /scan.
export const useScanAndPaySubmit = () => {
  const { t } = useResource();
  // Stays true once a bank redirect is committed — mutation isPending flips false before the navigation completes, which would re-enable the button and make the tap look like a no-op.
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { mutateAsync: scanPayPoint, isPending: isScanning } =
    useScanPayPoint();
  const { mutateAsync: payTransaction, isPending: isPaying } =
    useTransactionPay();
  const { mutate: fetchAndRedirectToPISPURL } = usePISPAuthenticate({
    onError: () => setIsRedirecting(false),
  });
  const lastScanRef = useRef<LastScan | null>(null);

  const submit = async ({
    paypointPublicID,
    bankAccount,
    amount,
    tipAmount,
    onError,
  }: SubmitArgs) => {
    try {
      const cached = lastScanRef.current;
      let transactionPublicID: string;

      if (
        cached &&
        cached.paypointPublicID === paypointPublicID &&
        cached.amount === amount &&
        cached.tipAmount === tipAmount
      ) {
        transactionPublicID = cached.transactionPublicID;
      } else {
        const scanResult = await scanPayPoint({
          publicID: paypointPublicID,
          amount,
          tipAmount,
        });
        transactionPublicID = scanResult.transactionPublicID;
        lastScanRef.current = {
          paypointPublicID,
          amount,
          tipAmount,
          transactionPublicID,
        };
      }

      await payTransaction(
        {
          publicID: transactionPublicID,
          senderBankAccountID: bankAccount.bankAccountID,
        },
        {
          onSuccess: ({ signingUrl, isServerAuthorized }) => {
            // useTransactionPay.onSuccess redirects (window.location.href); lock so the button stays loading through that navigation.
            if (signingUrl || isServerAuthorized) {
              setIsRedirecting(true);
            }
          },
          onError: (error) => {
            const e = error as unknown as ErrorType;
            if (e.data?.errors.pispAuthentication) {
              setIsRedirecting(true);
              fetchAndRedirectToPISPURL({
                environment: bankAccount.environment,
                transactionPublicID,
              });
            }
            onError(getTransactionErrorMessage(e, t));
          },
        },
      );
    } catch (e) {
      handleTransactionPayError(e as ErrorResponse, t);
    }
  };

  return {
    submit,
    isSubmitting: isScanning || isPaying || isRedirecting,
  };
};
